import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../config/api';
import { useToast } from '../context/ToastContext';
import { useDataCache } from '../context/DataCacheContext';

export default function useSitesList() {
  const { getCachedData, setCachedData } = useDataCache();
  const cached = getCachedData('sites');
  const [sites, setSites] = useState(cached || []);
  const [loading, setLoading] = useState(!cached);
  const skipNextLoadingRef = useRef(!!cached);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSiteName, setNewSiteName] = useState('');
  const [creating, setCreating] = useState(false);
  const [highlightedSiteId, setHighlightedSiteId] = useState(null);
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const fetchSites = useCallback(async () => {
    try {
      if (!skipNextLoadingRef.current) {
        setLoading(true);
      }
      const response = await apiRequest('/sites/');
      if (response.ok) {
        const sitesData = await response.json();
        setSites(sitesData);
        setCachedData('sites', sitesData);
      } else {
        throw new Error('Failed to fetch sites');
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
      setError(error.message);
      showError('Failed to fetch sites');
    } finally {
      setLoading(false);
      skipNextLoadingRef.current = false;
    }
  }, [showError, setCachedData]);

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  const handleCreateSite = async (e) => {
    e.preventDefault();
    if (!newSiteName.trim()) return;

    try {
      setCreating(true);
      const siteResponse = await apiRequest('/sites/', {
        method: 'POST',
        body: JSON.stringify({
          name: newSiteName,
        }),
      });

      if (!siteResponse.ok) {
        throw new Error('Failed to create site');
      }

      const newSite = await siteResponse.json();

      const pageResponse = await apiRequest('/pages/', {
        method: 'POST',
        body: JSON.stringify({
          title: 'Home',
          page_type: 'landing',
          content_type: 'tff_page_builder_json_simple',
          content: {},
          site_id: newSite.id,
          is_homepage: true,
        }),
      });

      if (!pageResponse.ok) {
        throw new Error('Failed to create homepage');
      }

      const homepage = await pageResponse.json();

      setShowCreateModal(false);
      setNewSiteName('');
      showSuccess('Site created successfully');
      navigate(`/pages/${homepage.id}/builder/`);
    } catch (error) {
      console.error('Error creating site:', error);
      showError('Failed to create site');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteSite = async (siteId) => {
    if (!window.confirm('Are you sure you want to delete this site? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await apiRequest(`/sites/${siteId}/`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setSites(prev => prev.filter(site => site.id !== siteId));
        showSuccess('Site deleted successfully');
      } else {
        throw new Error('Failed to delete site');
      }
    } catch (error) {
      console.error('Error deleting site:', error);
      showError('Failed to delete site');
    }
  };

  const handleDuplicateSite = async (siteId) => {
    try {
      const response = await apiRequest(`/sites/${siteId}/duplicate`, {
        method: 'POST',
      });

      if (response.ok) {
        const newSite = await response.json();
        setSites(prev => {
          // Insert the duplicate immediately after the original site
          const idx = prev.findIndex(s => s.id === siteId);
          const updated = [...prev];
          updated.splice(idx + 1, 0, newSite);
          setCachedData('sites', updated);
          return updated;
        });

        // Scroll to and briefly highlight the new site row
        setHighlightedSiteId(newSite.id);
        setTimeout(() => {
          const el = document.querySelector(`[data-site-id="${newSite.id}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
        setTimeout(() => setHighlightedSiteId(null), 2000);

        showSuccess('Site duplicated successfully');
      } else {
        throw new Error('Failed to duplicate site');
      }
    } catch (error) {
      console.error('Error duplicating site:', error);
      showError('Failed to duplicate site');
    }
  };

  const handlePublishToggle = async (siteId, currentStatus) => {
    try {
      const action = currentStatus ? 'unpublish' : 'publish';
      const response = await apiRequest(`/sites/${siteId}/${action}`, {
        method: 'POST',
      });

      if (response.ok) {
        const updatedSite = await response.json();
        setSites(prev => prev.map(site =>
          site.id === siteId ? updatedSite : site
        ));
        showSuccess(`Site ${action}ed successfully`);
      } else {
        throw new Error(`Failed to ${action} site`);
      }
    } catch (error) {
      console.error(`Error ${currentStatus ? 'unpublishing' : 'publishing'} site:`, error);
      showError(`Failed to ${currentStatus ? 'unpublish' : 'publish'} site`);
    }
  };

  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  }, []);

  return {
    // State
    sites,
    loading,
    error,
    showCreateModal,
    newSiteName,
    creating,
    highlightedSiteId,
    // Setters
    setShowCreateModal,
    setNewSiteName,
    // Actions
    fetchSites,
    handleCreateSite,
    handleDeleteSite,
    handleDuplicateSite,
    handlePublishToggle,
    formatDate,
  };
}
